import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { travelCariHref } from '@polatkan/api/travel-cari';
import { useTranslation } from 'react-i18next';
import { ArrowRightLeft, Loader2, Plus, Search, Trash2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog';
import { Combobox, type ComboboxOption } from '@/components/ui/combobox';
import { trpc } from '@/lib/trpc';
import { showSuccessToast, showErrorToast } from '@/hooks/use-mutation-with-toast';
import { MoveCariTypeDialog, type MoveCariTarget } from './move-cari-type-dialog';

const NONE = '__none__';

interface AssignedAccount {
  id: string;
  code: string;
  name: string;
}

interface CariTypeDetail {
  id: string;
  code: string;
  name: string;
  assignedAccounts: AssignedAccount[];
  source: string;
}

interface CariTypeDetailPanelProps {
  cariType: CariTypeDetail;
  accountOptions: ComboboxOption[];
  canEdit: boolean;
  canDelete: boolean;
  /** Opens the create-cari modal for this type; omitted when the user cannot create. */
  onAddCari?: () => void;
}

interface PendingAssignment {
  source: 'customer' | 'partner';
  cariId: string;
  accountId: string | null;
}

// Account pool + per-cari account assignment for one cari type.
export function CariTypeDetailPanel({
  cariType,
  accountOptions,
  canEdit,
  canDelete,
  onAddCari,
}: CariTypeDetailPanelProps) {
  const { t } = useTranslation('travelCari');
  const { t: tCommon } = useTranslation('common');
  const [poolIds, setPoolIds] = useState<string[]>([]);
  const [cariSearch, setCariSearch] = useState('');
  const [debouncedCariSearch, setDebouncedCariSearch] = useState('');
  const [pendingAssignment, setPendingAssignment] = useState<PendingAssignment | null>(null);
  const [movingCari, setMovingCari] = useState<MoveCariTarget | null>(null);
  const [cariToDelete, setCariToDelete] = useState<{
    id: string;
    source: 'customer' | 'partner';
    companyName: string;
  } | null>(null);

  useEffect(() => {
    setPoolIds(cariType.assignedAccounts.map((a) => a.id));
  }, [cariType.id, cariType.assignedAccounts]);

  useEffect(() => {
    const handle = setTimeout(() => setDebouncedCariSearch(cariSearch), 300);
    return () => clearTimeout(handle);
  }, [cariSearch]);

  const utils = trpc.useUtils();

  const { data: caris, isLoading: carisLoading } = trpc.travelCariType.carisWithAccounts.useQuery({
    cariTypeId: cariType.id,
    search: debouncedCariSearch || undefined,
  });

  const setAccountsMutation = trpc.travelCariType.setAccounts.useMutation({
    onSuccess: () => {
      utils.travelCariType.listDetailed.invalidate();
      utils.travelCariType.list.invalidate();
      showSuccessToast(t('cariTypes.poolUpdated'));
    },
    onError: (error) => showErrorToast(error.message),
  });

  // A move or delete changes this type's cari list AND the global cari views.
  const invalidateCariViews = () => {
    utils.travelCariType.carisWithAccounts.invalidate();
    utils.travelCariType.listDetailed.invalidate();
    utils.travelCariType.list.invalidate();
    utils.travelCari.cards.invalidate();
    utils.travelCari.cardCounts.invalidate();
  };

  const deleteMutation = trpc.travelCariType.deleteCari.useMutation({
    onSuccess: () => {
      invalidateCariViews();
      showSuccessToast(tCommon('deleted'));
      setCariToDelete(null);
    },
    onError: (error) => {
      setCariToDelete(null);
      showErrorToast(error.message);
    },
  });

  const assignMutation = trpc.travelCariType.assignCariAccount.useMutation({
    onSuccess: () => {
      utils.travelCariType.carisWithAccounts.invalidate({ cariTypeId: cariType.id });
      setPendingAssignment(null);
      showSuccessToast(t('cariTypes.accountAssigned'));
    },
    onError: (error, variables) => {
      if (error.message.startsWith('CONFIRM_REQUIRED')) {
        setPendingAssignment({
          source: variables.source,
          cariId: variables.cariId,
          accountId: variables.accountId,
        });
      } else {
        showErrorToast(error.message);
      }
    },
  });

  const accountById = useMemo(() => {
    const map = new Map<string, ComboboxOption>();
    for (const o of accountOptions) map.set(o.value, o);
    return map;
  }, [accountOptions]);

  const poolOptions = useMemo(
    () => poolIds.map((id) => accountById.get(id)).filter((o): o is ComboboxOption => !!o),
    [poolIds, accountById]
  );

  // Caris pick from the pool when one is configured, otherwise the whole chart.
  const cariAccountOptions = useMemo(() => {
    const base = poolOptions.length > 0 ? poolOptions : accountOptions;
    return [{ value: NONE, label: t('cariTypes.noAccount') }, ...base];
  }, [poolOptions, accountOptions, t]);

  const savedPoolIds = cariType.assignedAccounts.map((a) => a.id);
  const poolDirty =
    poolIds.length !== savedPoolIds.length || poolIds.some((id) => !savedPoolIds.includes(id));

  const handleAssign = (
    source: 'customer' | 'partner',
    cariId: string,
    accountId: string | null,
    confirmed = false
  ) => {
    assignMutation.mutate({ source, cariId, accountId, confirmed });
  };

  return (
    <div className="border-border bg-card border">
      <div className="border-border border-b px-4 py-3">
        <h3 className="font-semibold">
          {cariType.code} — {cariType.name}
        </h3>
      </div>

      {/* Account pool */}
      <div className="border-border space-y-3 border-b px-4 py-4">
        <div>
          <h4 className="text-sm font-medium">{t('cariTypes.accountPool')}</h4>
          <p className="text-muted-foreground text-xs">{t('cariTypes.accountPoolDesc')}</p>
        </div>

        {poolOptions.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {poolOptions.map((o) => (
              <span
                key={o.value}
                className="border-border bg-muted inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs"
              >
                {o.label}
                {canEdit && (
                  <button
                    type="button"
                    onClick={() => setPoolIds((prev) => prev.filter((id) => id !== o.value))}
                    className="text-muted-foreground hover:text-red-600"
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
              </span>
            ))}
          </div>
        )}

        {canEdit && (
          <div className="flex items-center gap-2">
            <div className="w-80">
              <Combobox
                options={accountOptions.filter((o) => !poolIds.includes(o.value))}
                value=""
                onValueChange={(value) => setPoolIds((prev) => [...prev, value])}
                placeholder={t('cariTypes.addAccount')}
                searchPlaceholder={t('cariTypes.searchAccount')}
              />
            </div>
            <Button
              size="sm"
              disabled={!poolDirty || setAccountsMutation.isPending}
              onClick={() =>
                setAccountsMutation.mutate({ cariTypeId: cariType.id, accountIds: poolIds })
              }
            >
              {setAccountsMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('cariTypes.savePool')}
            </Button>
          </div>
        )}
      </div>

      {/* Caris with account assignment */}
      <div className="space-y-3 px-4 py-4">
        <div className="flex items-center justify-between gap-4">
          <h4 className="text-sm font-medium">
            {t('cariTypes.caris')}
            {caris && <span className="text-muted-foreground ml-1.5">({caris.length})</span>}
          </h4>
          <div className="flex items-center gap-2">
            <div className="relative w-64">
              <Search className="text-muted-foreground absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2" />
              <input
                type="text"
                value={cariSearch}
                onChange={(e) => setCariSearch(e.target.value)}
                placeholder={t('cariTypes.searchCari')}
                className="border-border bg-background focus:border-foreground h-8 w-full border pl-8 pr-3 text-sm focus:outline-none"
              />
            </div>
            {onAddCari && (
              <Button size="sm" variant="outline" onClick={onAddCari}>
                <Plus className="mr-1.5 h-3.5 w-3.5" />
                {t('cariTypes.addCari')}
              </Button>
            )}
          </div>
        </div>

        {carisLoading && (
          <div className="text-muted-foreground flex items-center justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        )}

        {!carisLoading && (caris?.length ?? 0) === 0 && (
          <p className="text-muted-foreground py-6 text-center text-sm">{t('cariTypes.noCaris')}</p>
        )}

        {!carisLoading && (caris?.length ?? 0) > 0 && (
          <div className="divide-border border-border divide-y border-t">
            {caris!.map((cari) => (
              <div key={cari.id} className="flex items-center gap-3 py-2">
                <div className="min-w-0 flex-1">
                  <Link to={travelCariHref(cari.source, cari.id)} className="block truncate text-sm font-medium hover:underline">{cari.companyName}</Link>
                  <p className="text-muted-foreground text-xs">
                    {cari.legacyCariCode || '—'}
                    {!cari.isActive && ` · ${t('cariTypes.inactive')}`}
                  </p>
                </div>
                <div className="w-96 shrink-0">
                  <Combobox
                    options={cariAccountOptions}
                    value={cari.muhasebeAccount?.id ?? NONE}
                    onValueChange={(value) =>
                      handleAssign(cari.source, cari.id, value === NONE ? null : value)
                    }
                    placeholder={t('cariTypes.selectAccount')}
                    searchPlaceholder={t('cariTypes.searchAccount')}
                    disabled={!canEdit || assignMutation.isPending}
                  />
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  {canEdit && (
                    <button
                      type="button"
                      onClick={() =>
                        setMovingCari({
                          id: cari.id,
                          companyName: cari.companyName,
                          source: cari.source,
                        })
                      }
                      className="text-muted-foreground hover:text-primary p-1.5"
                      title={t('cariTypes.moveCari')}
                    >
                      <ArrowRightLeft className="h-4 w-4" />
                    </button>
                  )}
                  {canDelete && (
                    <button
                      type="button"
                      onClick={() =>
                        setCariToDelete({
                          id: cari.id,
                          source: cari.source,
                          companyName: cari.companyName,
                        })
                      }
                      className="text-muted-foreground p-1.5 hover:text-red-600"
                      title={tCommon('delete')}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Reassignment confirmation (cari already has GL postings) */}
      <ConfirmationDialog
        open={!!pendingAssignment}
        onOpenChange={(open) => {
          if (!open) setPendingAssignment(null);
        }}
        title={t('cariTypes.confirmReassignTitle')}
        description={t('cariTypes.confirmReassign')}
        confirmLabel={t('cariTypes.confirmReassignAction')}
        onConfirm={() => {
          if (pendingAssignment) {
            handleAssign(
              pendingAssignment.source,
              pendingAssignment.cariId,
              pendingAssignment.accountId,
              true
            );
          }
        }}
        isLoading={assignMutation.isPending}
      />

      {/* Move a cari to a different type */}
      <MoveCariTypeDialog
        cari={movingCari}
        currentTypeId={cariType.id}
        onOpenChange={(open) => {
          if (!open) setMovingCari(null);
        }}
        onMoved={invalidateCariViews}
      />

      {/* Delete confirmation (server blocks caris with documents or ledger history) */}
      <ConfirmationDialog
        open={!!cariToDelete}
        onOpenChange={(open) => {
          if (!open) setCariToDelete(null);
        }}
        title={tCommon('delete')}
        description={t('cariTypes.deleteCariConfirm', { name: cariToDelete?.companyName ?? '' })}
        confirmLabel={tCommon('delete')}
        variant="destructive"
        onConfirm={() => {
          if (cariToDelete) {
            deleteMutation.mutate({ id: cariToDelete.id, source: cariToDelete.source });
          }
        }}
        isLoading={deleteMutation.isPending}
      />
    </div>
  );
}
